// frontend/src/contexts/WebSocketContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { io } from 'socket.io-client'
import { useAuth } from '@hooks/useAuth'

export const WebSocketContext = createContext({})

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const { user, token } = useAuth()
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5

  useEffect(() => {
    if (!user || !token) return

    const connectWebSocket = () => {
      const sessionId = user.id || 'anonymous'
      const wsUrl = `wss:/api.testcrew.ai/ws/${sessionId}`
      
      try {
        const newSocket = new WebSocket(wsUrl)
        
        newSocket.onopen = () => {
          console.log('WebSocket connected')
          setIsConnected(true)
          setConnectionError(null)
          reconnectAttempts.current = 0
        }
        
        newSocket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            
            // Dispatch custom event for components to listen to
            const customEvent = new CustomEvent('websocket-message', {
              detail: message
            })
            window.dispatchEvent(customEvent)
            
            console.log('WebSocket message received:', message)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }
        
        newSocket.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason)
          setIsConnected(false)
          setSocket(null)
          
          // Attempt to reconnect
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++
            console.log(`Attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`)
            setTimeout(connectWebSocket, 3000 * reconnectAttempts.current)
          } else {
            setConnectionError('Failed to connect after multiple attempts')
          }
        }
        
        newSocket.onerror = (error) => {
          console.error('WebSocket error:', error)
          setConnectionError('WebSocket connection error')
        }
        
        setSocket(newSocket)
        
      } catch (error) {
        console.error('Failed to create WebSocket connection:', error)
        setConnectionError('Failed to create WebSocket connection')
      }
    }

    connectWebSocket()

    return () => {
      if (socket) {
        socket.close()
      }
    }
  }, [user, token])

  const sendMessage = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message))
      console.log('WebSocket message sent:', message)
    } else {
      console.error('WebSocket is not connected')
      setConnectionError('WebSocket is not connected')
    }
  }

  const value = {
    socket,
    isConnected,
    connectionError,
    sendMessage
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

// Custom hook to use WebSocket
export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

